/**
 * Registre déclaratif des traceurs d'un site publié.
 *
 * Chaque entrée porte ce que la politique cookies doit annoncer : émetteur,
 * finalité, nature de ce qui est écrit sur le terminal, durée, transfert hors
 * UE, politique de confidentialité du tiers.
 *
 * Les identifiants des services tiers sont ceux du catalogue d'embeds
 * (`blocks/web/EmbedBlock/embedCatalog.js`) et des fournisseurs vidéo
 * (`media/embedProviders.js`). Un test verrouille cette correspondance :
 * ajouter un service à l'un de ces deux catalogues sans le déclarer ici fait
 * échouer la suite. C'est voulu — un traceur non déclaré est un traceur non
 * annoncé au lecteur.
 *
 * Module pur, sans React.
 */

/** Finalités. Une finalité = une case indépendante dans le panneau. */
export const CONSENT_CATEGORIES = {
  necessary: 'necessary',
  embeds: 'embeds',
  reading: 'reading',
};

/** Libellés du panneau de préférences, dans l'ordre d'affichage. */
export const CATEGORY_ORDER = [
  CONSENT_CATEGORIES.necessary,
  CONSENT_CATEGORIES.embeds,
  CONSENT_CATEGORIES.reading,
];

export const CATEGORY_LABELS = {
  [CONSENT_CATEGORIES.necessary]: {
    title: 'Strictement nécessaires',
    description: "Indispensables au fonctionnement du site : votre session, la mémorisation de votre choix ci-contre. Ils ne peuvent pas être désactivés, et ne servent à rien d'autre.",
  },
  [CONSENT_CATEGORIES.embeds]: {
    title: 'Contenus tiers',
    description: 'Vidéos, cartes, formulaires et agendas intégrés dans ce document. Leurs éditeurs déposent leurs propres traceurs. Sans votre accord, ces contenus restent masqués et vous pouvez les afficher un par un.',
  },
  [CONSENT_CATEGORIES.reading]: {
    title: 'Suivi de lecture',
    description: "Permet à l'émetteur de ce document de savoir quelles pages vous avez consultées, quelles parties de ces pages ont retenu votre attention, et quelles vidéos vous avez lancées. Refuser ne retire rien à ce que vous pouvez lire.",
  },
};

/**
 * Émetteur par défaut des éléments propres au site. Le manifeste le remplace
 * par la société émettrice, responsable de traitement, quand elle est connue :
 * Wiiz, éditeur de Smart4, n'héberge le site que pour son compte (consentManifest.js).
 */
export const PROCESSOR_NAME = 'Wiiz';

/**
 * Durée du suivi de lecture, annoncée ici et dans la notice « Vos données ».
 * Appliquée par PurgeWebTrackingEventsWorker (dépôt web) : les deux se changent
 * ensemble.
 */
export const READING_RETENTION = 'Jusqu’à 6 mois après la fin de votre accès à ce document (expiration, retrait ou dépublication), et au plus 3 ans après chaque mesure';
const GOOGLE = 'Google Ireland Limited';

/**
 * Traceurs présents sur tout site publié, quel qu'en soit le contenu.
 *
 * Tous nécessaires ou exemptés, **sauf `reading-tracking`** : le suivi de
 * lecture est armé sur tous les sites, mais il ne collecte rien tant que le
 * lecteur ne l'a pas accepté. C'est sa présence ici qui fait apparaître la case
 * « Suivi de lecture » dans le panneau — une finalité sans traceur n'est jamais
 * proposée (voir consentManifest.js).
 */
export const BASELINE_TRACKER_IDS = [
  'viewer-session',
  'consent-choice',
  'loading-splash',
  'google-fonts',
  'inter-font',
  'reading-tracking',
];

/**
 * Le registre. `storage` décrit ce qui est écrit sur le terminal, en langage
 * non technique : c'est ce que le lecteur lit dans la politique cookies.
 */
export const TRACKER_CATALOG = {
  // ── Socle, toujours présent ───────────────────────────────────────────────
  'viewer-session': {
    id: 'viewer-session',
    label: 'Session de consultation',
    category: CONSENT_CATEGORIES.necessary,
    issuer: PROCESSOR_NAME,
    purpose: 'Vous maintenir connecté au document pendant votre visite',
    storage: 'Un jeton de connexion, conservé par votre navigateur',
    retention: 'Jusqu’à la fermeture de l’onglet',
    outsideEu: false,
    privacyUrl: null,
  },
  'consent-choice': {
    id: 'consent-choice',
    label: 'Mémorisation de votre choix',
    category: CONSENT_CATEGORIES.necessary,
    issuer: PROCESSOR_NAME,
    purpose: 'Ne pas vous redemander votre choix à chaque visite',
    storage: 'Un cookie contenant les finalités que vous avez acceptées et refusées',
    retention: '6 mois',
    outsideEu: false,
    privacyUrl: null,
  },
  'loading-splash': {
    id: 'loading-splash',
    label: "Écran d'accueil déjà vu",
    category: CONSENT_CATEGORIES.necessary,
    issuer: PROCESSOR_NAME,
    purpose: "Ne pas rejouer l'animation d'accueil à chaque page",
    storage: 'Une marque indiquant que l’écran d’accueil vous a déjà été présenté',
    retention: 'Jusqu’à la fermeture de l’onglet',
    outsideEu: false,
    privacyUrl: null,
  },
  'google-fonts': {
    id: 'google-fonts',
    label: 'Polices de caractères',
    category: CONSENT_CATEGORIES.necessary,
    issuer: GOOGLE,
    purpose: 'Affichage du document avec les polices choisies par son auteur',
    storage: 'Rien sur votre terminal — seule une feuille de style est téléchargée depuis les serveurs de Google',
    retention: 'Sans objet',
    outsideEu: true,
    privacyUrl: 'https://policies.google.com/privacy',
  },
  // Découvert au navigateur, pas dans le code : la feuille de style de Puck
  // (`@puckeditor/core/puck.css`, importée par WebViewer.jsx) contient un
  // `@import url(https://rsms.me/inter/inter.css)`. Aucun dérivé du contenu ne
  // pouvait le voir — d'où l'intérêt d'avoir regardé le trafic réel.
  'inter-font': {
    id: 'inter-font',
    label: 'Police Inter',
    category: CONSENT_CATEGORIES.necessary,
    issuer: 'rsms.me (Rasmus Andersson)',
    purpose: "Affichage de l'interface du document",
    storage: 'Rien sur votre terminal — seule une feuille de style est téléchargée depuis rsms.me',
    retention: 'Sans objet',
    outsideEu: true,
    privacyUrl: 'https://rsms.me/inter/',
  },
  // ── Contenus tiers, présents seulement si le document en contient ────────
  calendly: {
    id: 'calendly',
    label: 'Calendly',
    category: CONSENT_CATEGORIES.embeds,
    issuer: 'Calendly LLC',
    purpose: 'Prise de rendez-vous intégrée',
    storage: 'Des cookies déposés par calendly.com au moment où le contenu s’affiche',
    retention: "Jusqu'à 2 ans",
    outsideEu: true,
    privacyUrl: 'https://calendly.com/privacy',
  },
  loom: {
    id: 'loom',
    label: 'Loom',
    category: CONSENT_CATEGORIES.embeds,
    issuer: 'Loom, Inc.',
    purpose: 'Lecture de vidéo enregistrée',
    storage: 'Des cookies déposés par loom.com au moment où le contenu s’affiche',
    retention: "Jusqu'à 1 an",
    outsideEu: true,
    privacyUrl: 'https://www.loom.com/privacy',
  },
  figma: {
    id: 'figma',
    label: 'Figma',
    category: CONSENT_CATEGORIES.embeds,
    issuer: 'Figma, Inc.',
    purpose: 'Affichage de maquette ou de prototype',
    storage: 'Des cookies déposés par figma.com au moment où le contenu s’affiche',
    retention: "Jusqu'à 1 an",
    outsideEu: true,
    privacyUrl: 'https://www.figma.com/legal/privacy/',
  },
  'google-slides': {
    id: 'google-slides',
    label: 'Google Slides',
    category: CONSENT_CATEGORIES.embeds,
    issuer: GOOGLE,
    purpose: 'Affichage de présentation',
    storage: 'Des cookies déposés par google.com au moment où le contenu s’affiche',
    retention: "Jusqu'à 2 ans",
    outsideEu: true,
    privacyUrl: 'https://policies.google.com/privacy',
  },
  'google-maps': {
    id: 'google-maps',
    label: 'Google Maps',
    category: CONSENT_CATEGORIES.embeds,
    issuer: GOOGLE,
    purpose: 'Affichage de carte',
    storage: 'Des cookies déposés par google.com au moment où le contenu s’affiche',
    retention: "Jusqu'à 2 ans",
    outsideEu: true,
    privacyUrl: 'https://policies.google.com/privacy',
  },
  typeform: {
    id: 'typeform',
    label: 'Typeform',
    category: CONSENT_CATEGORIES.embeds,
    issuer: 'TYPEFORM SL (Espagne)',
    purpose: 'Affichage de formulaire',
    storage: 'Des cookies déposés par typeform.com au moment où le contenu s’affiche',
    retention: "Jusqu'à 1 an",
    outsideEu: false,
    privacyUrl: 'https://www.typeform.com/legal/privacy-policy/',
  },
  tally: {
    id: 'tally',
    label: 'Tally',
    category: CONSENT_CATEGORIES.embeds,
    issuer: 'Tally BV (Belgique)',
    purpose: 'Affichage de formulaire',
    storage: 'Des cookies déposés par tally.so au moment où le contenu s’affiche',
    retention: "Jusqu'à 1 an",
    outsideEu: false,
    privacyUrl: 'https://tally.so/help/privacy-policy',
  },
  youtube: {
    id: 'youtube',
    label: 'YouTube',
    category: CONSENT_CATEGORIES.embeds,
    issuer: GOOGLE,
    purpose: "Lecture de vidéo, et remontée de votre progression à l'émetteur du document si vous avez accepté le suivi de lecture",
    storage: 'Le mode sans cookie de YouTube est utilisé : rien n’est déposé tant que vous ne lancez pas la lecture. Le script de pilotage du lecteur est chargé depuis youtube.com',
    retention: "Jusqu'à 6 mois",
    outsideEu: true,
    privacyUrl: 'https://policies.google.com/privacy',
  },
  vimeo: {
    id: 'vimeo',
    label: 'Vimeo',
    category: CONSENT_CATEGORIES.embeds,
    issuer: 'Vimeo.com, Inc.',
    purpose: "Lecture de vidéo, mesure d'audience du lecteur vidéo, et remontée de votre progression à l'émetteur du document si vous avez accepté le suivi de lecture",
    storage: 'Des cookies déposés par vimeo.com au moment où la vidéo s’affiche',
    retention: "Jusqu'à 2 ans",
    outsideEu: true,
    privacyUrl: 'https://vimeo.com/privacy',
  },

  // ── Suivi de lecture ─────────────────────────────────────────────────────
  // Le seul traceur du socle qui demande un consentement. Il remplace le
  // « journal des accès » de la première version de ce catalogue, qui annonçait
  // une finalité — détecter un accès partagé — qu'aucun code ne poursuivait.
  'reading-tracking': {
    id: 'reading-tracking',
    label: 'Suivi de lecture',
    category: CONSENT_CATEGORIES.reading,
    issuer: PROCESSOR_NAME,
    purpose: "Indiquer à l'émetteur de ce document quelles pages vous avez consultées, combien de temps et jusqu'où vous les avez lues, quelles parties de ces pages sont restées devant vos yeux, quels liens vous avez cliqués, quelles vidéos vous avez lancées et jusqu'où vous les avez regardées, et sur quel type d'écran (mobile, tablette ou ordinateur) vous avez lu",
    storage: 'Rien de nouveau sur votre terminal : la mesure s’appuie sur la session déjà ouverte par votre connexion',
    retention: READING_RETENTION,
    outsideEu: false,
    privacyUrl: null,
  },
};

export function trackerById(id) {
  return TRACKER_CATALOG[id] || null;
}

/** Identifiants déclarés, hors socle — c'est-à-dire les services tiers. */
export function thirdPartyTrackerIds() {
  return Object.keys(TRACKER_CATALOG).filter((id) => !BASELINE_TRACKER_IDS.includes(id));
}

const SENDER_PHRASE = /l['’]émetteur (?:de ce|du) document/g;

/**
 * Le lecteur doit savoir **qui** suit sa lecture avant d'y consentir (CEPD,
 * lignes directrices 05/2020 §64) : partout où le catalogue parle de
 * « l'émetteur de ce document », on écrit le nom de la société quand on le
 * connaît.
 */
export function nameSender(text, controllerName) {
  if (!controllerName || typeof text !== 'string') return text;
  return text.replace(SENDER_PHRASE, controllerName);
}

/** Titre et description d'une finalité, nommant la société émettrice. */
export function categoryLabel(category, controllerName) {
  const { title, description } = CATEGORY_LABELS[category];
  return { title, description: nameSender(description, controllerName) };
}
